import { useState, useEffect } from 'react';
import { useParams } from 'react-router-dom';
import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../components/ui/select';
import { Loader2, CheckCircle, Building, Plus, Trash2, FileText, AlertTriangle } from 'lucide-react';
import { toast } from 'sonner';

const API_URL = process.env.REACT_APP_BACKEND_URL;

const emptyTimeshare = () => ({
  resort_name: '', city: '', state: '', type: 'week',
  maintenance_fee: '', annual_increase_percent: '', has_balance: false, balance_amount: '',
});

const SessionInfoPage = () => {
  const { token } = useParams();
  const [loading, setLoading] = useState(true);
  const [submitting, setSubmitting] = useState(false);
  const [submitted, setSubmitted] = useState(false);
  const [error, setError] = useState(null);
  const [clientData, setClientData] = useState(null);

  const [formData, setFormData] = useState({
    name: '', email: '', phone: '', address: '',
    number_of_timeshares: 1,
    timeshares: [emptyTimeshare()],
  });

  useEffect(() => {
    const load = async () => {
      try {
        const res = await fetch(`${API_URL}/api/sessions/${token}/info`);
        if (!res.ok) {
          const err = await res.json();
          setError(err.detail || 'Invalid or expired link');
          setLoading(false);
          return;
        }
        const data = await res.json();
        setClientData(data);
        setFormData(prev => ({
          ...prev,
          name: data.client_name || '',
          email: data.client_email || '',
          phone: data.client_phone || '',
          address: data.client_address || '',
          timeshares: data.existing_timeshares?.length > 0
            ? data.existing_timeshares.map(t => ({
                resort_name: t.resort_name || '', city: t.city || '', state: t.state || '',
                type: t.type || 'week', maintenance_fee: t.maintenance_fee || '',
                annual_increase_percent: t.annual_increase_percent || '',
                has_balance: t.has_balance || false, balance_amount: t.balance_amount || '',
              }))
            : [emptyTimeshare()],
          number_of_timeshares: data.existing_timeshares?.length || 1,
        }));
        if (data.used) setSubmitted(true);
      } catch {
        setError('Unable to load form. Please try again later.');
      }
      setLoading(false);
    };
    load();
  }, [token]);

  const updateTimeshare = (idx, field, value) => {
    setFormData(prev => {
      const ts = [...prev.timeshares];
      ts[idx] = { ...ts[idx], [field]: value };
      return { ...prev, timeshares: ts };
    });
  };

  const addTimeshare = () => {
    setFormData(prev => ({
      ...prev,
      timeshares: [...prev.timeshares, emptyTimeshare()],
      number_of_timeshares: prev.timeshares.length + 1,
    }));
  };

  const removeTimeshare = (idx) => {
    if (formData.timeshares.length <= 1) return;
    setFormData(prev => ({
      ...prev,
      timeshares: prev.timeshares.filter((_, i) => i !== idx),
      number_of_timeshares: prev.timeshares.length - 1,
    }));
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!formData.name.trim()) { toast.error('Name is required'); return; }
    if (!formData.timeshares[0]?.resort_name) { toast.error('At least one resort name is required'); return; }

    setSubmitting(true);
    try {
      const body = {
        name: formData.name,
        email: formData.email,
        phone: formData.phone,
        address: formData.address,
        number_of_timeshares: formData.timeshares.length,
        timeshares: formData.timeshares.map(t => ({
          resort_name: t.resort_name,
          city: t.city,
          state: t.state,
          type: t.type,
          maintenance_fee: t.maintenance_fee ? parseFloat(t.maintenance_fee) : null,
          annual_increase_percent: t.annual_increase_percent ? parseFloat(t.annual_increase_percent) : null,
          has_balance: t.has_balance,
          balance_amount: t.has_balance && t.balance_amount ? parseFloat(t.balance_amount) : null,
        })),
      };
      const res = await fetch(`${API_URL}/api/sessions/${token}/info`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(body),
      });
      if (!res.ok) {
        const err = await res.json();
        toast.error(err.detail || 'Submission failed');
        return;
      }
      setSubmitted(true);
      toast.success('Information submitted successfully!');
    } catch {
      toast.error('Submission failed. Please try again.');
    } finally {
      setSubmitting(false);
    }
  };

  if (loading) return (
    <div className="min-h-screen bg-gradient-to-br from-slate-50 to-slate-100 flex items-center justify-center">
      <Loader2 className="h-8 w-8 animate-spin text-teal-600" />
    </div>
  );

  if (error) return (
    <div className="min-h-screen bg-gradient-to-br from-slate-50 to-slate-100 flex items-center justify-center p-6">
      <Card className="max-w-md w-full text-center">
        <CardContent className="py-12">
          <AlertTriangle className="h-12 w-12 text-amber-500 mx-auto mb-4" />
          <h2 className="text-xl font-bold text-slate-800 mb-2">Link Unavailable</h2>
          <p className="text-slate-500">{error}</p>
        </CardContent>
      </Card>
    </div>
  );

  if (submitted) return (
    <div className="min-h-screen bg-gradient-to-br from-slate-50 to-slate-100 flex items-center justify-center p-6">
      <Card className="max-w-md w-full text-center" data-testid="info-submitted">
        <CardContent className="py-12">
          <div className="w-16 h-16 bg-emerald-100 rounded-full flex items-center justify-center mx-auto mb-6">
            <CheckCircle className="h-8 w-8 text-emerald-600" />
          </div>
          <h2 className="text-xl font-bold text-slate-800 mb-2">Thank You!</h2>
          <p className="text-slate-500">Your information has been submitted successfully. Our team will review it and contact you soon.</p>
        </CardContent>
      </Card>
    </div>
  );

  return (
    <div className="min-h-screen bg-gradient-to-br from-slate-50 to-slate-100 py-8 px-4" data-testid="session-info-page">
      <div className="max-w-2xl mx-auto space-y-6">
        <div className="text-center mb-8">
          <h1 className="text-2xl font-bold text-slate-800">Timeshare Information Request</h1>
          <p className="text-slate-500 mt-1">Please provide details about your timeshare ownership</p>
        </div>

        <form onSubmit={handleSubmit}>
          {/* Client Info */}
          <Card className="mb-6">
            <CardHeader className="pb-3"><CardTitle className="text-base flex items-center gap-2"><FileText className="h-4 w-4 text-teal-600" />Your Information</CardTitle></CardHeader>
            <CardContent className="grid grid-cols-1 sm:grid-cols-2 gap-4">
              <div><Label className="text-xs">Full Name *</Label><Input value={formData.name} onChange={e => setFormData({...formData, name: e.target.value})} placeholder="John Smith" data-testid="info-name" /></div>
              <div><Label className="text-xs">Email</Label><Input type="email" value={formData.email} onChange={e => setFormData({...formData, email: e.target.value})} placeholder="john@email.com" data-testid="info-email" /></div>
              <div><Label className="text-xs">Phone</Label><Input value={formData.phone} onChange={e => setFormData({...formData, phone: e.target.value})} placeholder="(555) 123-4567" data-testid="info-phone" /></div>
              <div><Label className="text-xs">Address</Label><Input value={formData.address} onChange={e => setFormData({...formData, address: e.target.value})} placeholder="123 Main St, City, ST 12345" data-testid="info-address" /></div>
            </CardContent>
          </Card>

          {/* Timeshares */}
          {formData.timeshares.map((ts, idx) => (
            <Card key={ts.id || `ts-${idx}`} className="mb-4" data-testid={`timeshare-card-${idx}`}>
              <CardHeader className="pb-3">
                <div className="flex items-center justify-between">
                  <CardTitle className="text-base flex items-center gap-2">
                    <Building className="h-4 w-4 text-teal-600" />
                    Timeshare {formData.timeshares.length > 1 ? `#${idx + 1}` : 'Details'}
                  </CardTitle>
                  {formData.timeshares.length > 1 && (
                    <Button type="button" variant="ghost" size="sm" className="text-red-500 hover:text-red-700 h-7" onClick={() => removeTimeshare(idx)}>
                      <Trash2 className="h-3.5 w-3.5" />
                    </Button>
                  )}
                </div>
              </CardHeader>
              <CardContent className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                <div className="sm:col-span-2"><Label className="text-xs">Resort Name *</Label><Input value={ts.resort_name} onChange={e => updateTimeshare(idx, 'resort_name', e.target.value)} placeholder="Marriott Vacation Club" data-testid={`ts-resort-${idx}`} /></div>
                <div><Label className="text-xs">City</Label><Input value={ts.city} onChange={e => updateTimeshare(idx, 'city', e.target.value)} placeholder="Orlando" /></div>
                <div><Label className="text-xs">State</Label><Input value={ts.state} onChange={e => updateTimeshare(idx, 'state', e.target.value)} placeholder="FL" /></div>
                <div>
                  <Label className="text-xs">Type</Label>
                  <Select value={ts.type} onValueChange={v => updateTimeshare(idx, 'type', v)}>
                    <SelectTrigger><SelectValue /></SelectTrigger>
                    <SelectContent>
                      <SelectItem value="week">Week</SelectItem>
                      <SelectItem value="points">Points</SelectItem>
                    </SelectContent>
                  </Select>
                </div>
                <div><Label className="text-xs">Annual Maintenance Fee</Label><Input type="number" value={ts.maintenance_fee} onChange={e => updateTimeshare(idx, 'maintenance_fee', e.target.value)} placeholder="1200" /></div>
                <div><Label className="text-xs">Annual Increase %</Label><Input type="number" value={ts.annual_increase_percent} onChange={e => updateTimeshare(idx, 'annual_increase_percent', e.target.value)} placeholder="3" /></div>
                <div className="sm:col-span-2">
                  <div className="flex items-center gap-3 mb-2">
                    <input type="checkbox" checked={ts.has_balance} onChange={e => updateTimeshare(idx, 'has_balance', e.target.checked)} className="rounded" id={`balance-${idx}`} />
                    <Label htmlFor={`balance-${idx}`} className="text-xs cursor-pointer">Has outstanding balance/loan</Label>
                  </div>
                  {ts.has_balance && (
                    <Input type="number" value={ts.balance_amount} onChange={e => updateTimeshare(idx, 'balance_amount', e.target.value)} placeholder="Balance amount" />
                  )}
                </div>
              </CardContent>
            </Card>
          ))}

          <Button type="button" variant="outline" className="w-full mb-6 gap-2" onClick={addTimeshare} data-testid="add-timeshare-btn">
            <Plus className="h-4 w-4" /> Add Another Timeshare
          </Button>

          <Button type="submit" className="w-full bg-teal-600 hover:bg-teal-700 text-white h-12 text-base gap-2" disabled={submitting} data-testid="submit-info-btn">
            {submitting ? <Loader2 className="h-5 w-5 animate-spin" /> : <CheckCircle className="h-5 w-5" />}
            {submitting ? 'Submitting...' : 'Submit Information'}
          </Button>
        </form>
      </div>
    </div>
  );
};

export default SessionInfoPage;
